import type { ReferenceRates } from "@ravnar/api-contract";

/** An amount in one currency. */
export interface CurrencyAmount {
	readonly currency: string;
	readonly amount: number;
}

/**
 * How many days old reference rates may be before a page says so: more than the ECB's longest normal gap between
 * publications, over Easter, which is five days.
 */
export const RATES_STALE_AFTER_DAYS = 7;

/** How many of `currency` one euro buys at the rates: its rate, 1 for the euro itself, or undefined when unquoted. */
function perEuro(rates: ReferenceRates, currency: string): number | undefined {
	if (currency === rates.base) {
		return 1;
	}
	const rate = rates.rates[currency];
	return rate === undefined ? undefined : Number(rate);
}

/**
 * How many of `to` one `from` buys at the rates, through the euro they are quoted against: 1 GBP = 1.1403 / 0.86045
 * USD. Undefined when the rates do not quote either currency.
 */
export function crossRate(rates: ReferenceRates, from: string, to: string): number | undefined {
	const fromRate = perEuro(rates, from);
	const toRate = perEuro(rates, to);
	return fromRate === undefined || toRate === undefined ? undefined : toRate / fromRate;
}

/** A currency's total, the rate that converts it, and what it comes to in the target currency. */
export interface ConvertedAmount extends CurrencyAmount {
	readonly rate: number;
	readonly converted: number;
}

/** What several currencies' totals come to in one of them. */
export interface Conversion {
	readonly target: string;
	/** The sum of every converted amount, unrounded: rounded once where it is shown, never from rounded parts. */
	readonly total: number;
	/** The totals the rates convert, worth the most first and then by code. */
	readonly converted: readonly ConvertedAmount[];
	/** The totals in a currency the rates do not quote, by code: left out of `total`, never counted as nothing. */
	readonly unconverted: readonly CurrencyAmount[];
}

/** Converts each currency's total into `target` at the rates (`crossRate`), and adds up what converts. */
export function convertTotals(totals: readonly CurrencyAmount[], rates: ReferenceRates, target: string): Conversion {
	const converted: ConvertedAmount[] = [];
	const unconverted: CurrencyAmount[] = [];
	let total = 0;
	for (const each of totals) {
		const rate = crossRate(rates, each.currency, target);
		if (rate === undefined) {
			unconverted.push(each);
		} else {
			converted.push({ ...each, rate, converted: each.amount * rate });
			total += each.amount * rate;
		}
	}
	return {
		target,
		total,
		converted: converted.toSorted((a, b) => b.converted - a.converted || a.currency.localeCompare(b.currency)),
		unconverted: unconverted.toSorted((a, b) => a.currency.localeCompare(b.currency)),
	};
}

/** How many whole days lie between the rates' day and `now`'s, in UTC. */
export function ratesAgeInDays(date: string, now: number): number {
	const today = new Date(now);
	const startOfToday = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
	return Math.round((startOfToday - Date.parse(`${date}T00:00:00Z`)) / (24 * 60 * 60 * 1000));
}
